import React, { useState, useEffect } from 'react';
import { LogOut, User, Settings, Bell, Shield, CreditCard, Users, FileText, Globe, Moon, Sun } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useDarkMode } from '../hooks/useDarkMode';
import { CustomRule, Collaborator } from '../types';
import { customRuleService, collaboratorService } from '../services/database';
import { ConfirmModal } from '../components/ConfirmModal';

export const SettingsPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [customRules, setCustomRules] = useState<CustomRule[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [currency, setCurrency] = useState('USD');
  const [timezone, setTimezone] = useState('UTC');
  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddCollaborator, setShowAddCollaborator] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'rule' | 'collaborator' | null, id: string | null }>({ type: null, id: null });

  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    merchant_pattern: '',
    category: '',
    classification: 'personal' as 'personal' | 'business',
    priority: '0',
  });

  const [collaboratorForm, setCollaboratorForm] = useState({
    email: '',
    role: 'viewer' as 'viewer' | 'editor' | 'admin',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rulesData, collaboratorsData] = await Promise.all([
        customRuleService.getCustomRules(),
        collaboratorService.getCollaborators(),
      ]);
      setCustomRules(rulesData);
      setCollaborators(collaboratorsData);
    } catch (error) {
      console.error('Error loading settings data:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDeleteRule = (id: string) => setConfirmDelete({ type: 'rule', id });
  const handleDeleteCollaborator = (id: string) => setConfirmDelete({ type: 'collaborator', id });
  const confirmDeleteAction = async () => {
    if (!confirmDelete.id || !confirmDelete.type) return;
    try {
      if (confirmDelete.type === 'rule') {
        await customRuleService.deleteCustomRule(confirmDelete.id);
      } else if (confirmDelete.type === 'collaborator') {
        await collaboratorService.deleteCollaborator(confirmDelete.id);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setConfirmDelete({ type: null, id: null });
    }
  };
  const cancelDelete = () => setConfirmDelete({ type: null, id: null });

  const futureIntegrations = [
    {
      icon: CreditCard,
      title: 'Bank Integration',
      description: 'Connect your bank accounts for automatic transaction sync',
      comingSoon: true,
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Get alerts for large transactions and financial insights',
      comingSoon: true,
    },
    {
      icon: Shield,
      title: 'Data Export',
      description: 'Export your financial data to CSV or PDF',
      comingSoon: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Manage your account and preferences
        </p>
      </div>

      {/* User Profile Section */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-primary-600" />
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Account Information</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Account Actions</h2>
        <div className="space-y-3">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-between p-3 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <LogOut className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Sign Out</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Log out of your account</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Personalization Settings */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Settings className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Personalization</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Dark Mode</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Switch between light and dark themes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Sun className={`w-4 h-4 ${!darkMode ? 'text-yellow-500' : 'text-gray-400'}`} />
              <button
                onClick={toggleDarkMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-green-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <Moon className={`w-4 h-4 ${darkMode ? 'text-blue-500' : 'text-gray-400'}`} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (C$)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Custom Rules */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Custom Rules</h2>
          </div>
          <button
            onClick={() => setShowAddRule(true)}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
          >
            Add Rule
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Create rules to automatically categorize transactions based on merchant names.
        </p>
        
        <div className="space-y-3">
          {customRules.map((rule) => (
            <div key={rule.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{rule.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {rule.merchant_pattern} → {rule.category_id || 'Default'} ({rule.classification})
                </p>
              </div>
              <button
                onClick={() => handleDeleteRule(rule.id)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Delete
              </button>
            </div>
          ))}
          {customRules.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No custom rules yet</p>
          )}
        </div>
      </div>

      {/* Collaborators */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Collaborators</h2>
          </div>
          <button
            onClick={() => setShowAddCollaborator(true)}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
          >
            Invite
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Invite partners, accountants, or assistants to view or manage your finances.
        </p>
        
        <div className="space-y-3">
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{collaborator.collaborator_email}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {collaborator.role} • {collaborator.is_active ? 'Active' : 'Pending'}
                </p>
              </div>
              <button
                onClick={() => handleDeleteCollaborator(collaborator.id)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
          ))}
          {collaborators.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No collaborators yet</p>
          )}
        </div>
      </div>

      {/* Future Integrations */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Globe className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Future Integrations</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          We're working on new features to make your financial tracking even better.
        </p>
        
        <div className="space-y-3">
          {futureIntegrations.map((integration, index) => {
            const Icon = integration.icon;
            return (
              <div
                key={index}
                className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{integration.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{integration.description}</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full">
                  Coming Soon
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* App Information */}
      <div className="card dark:bg-gray-900 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">App Information</h2>
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex justify-between">
            <span>Version</span>
            <span>1.0.0</span>
          </div>
          <div className="flex justify-between">
            <span>Last Updated</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Add Custom Rule Modal */}
      {showAddRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Custom Rule</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await customRuleService.createCustomRule({
                  user_id: 'user1',
                  name: ruleForm.name,
                  description: ruleForm.description,
                  merchant_pattern: ruleForm.merchant_pattern,
                  category_id: ruleForm.category || undefined,
                  classification: ruleForm.classification,
                  is_active: true,
                  priority: parseInt(ruleForm.priority),
                });
                setShowAddRule(false);
                setRuleForm({
                  name: '',
                  description: '',
                  merchant_pattern: '',
                  category: '',
                  classification: 'personal',
                  priority: '0',
                });
                loadData();
              } catch (error) {
                console.error('Error creating custom rule:', error);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Merchant Pattern</label>
                <input
                  type="text"
                  value={ruleForm.merchant_pattern}
                  onChange={(e) => setRuleForm({ ...ruleForm, merchant_pattern: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., TELUS, Starbucks"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={ruleForm.category}
                  onChange={(e) => setRuleForm({ ...ruleForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Utilities, Food & Dining"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
                <select
                  value={ruleForm.classification}
                  onChange={(e) => setRuleForm({ ...ruleForm, classification: e.target.value as 'personal' | 'business' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="personal">Personal</option>
                  <option value="business">Business</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Rule
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddRule(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Collaborator Modal */}
      {showAddCollaborator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Invite Collaborator</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await collaboratorService.createCollaborator({
                  user_id: 'user1',
                  collaborator_email: collaboratorForm.email,
                  role: collaboratorForm.role,
                  is_active: true,
                  invited_at: new Date().toISOString(),
                });
                setShowAddCollaborator(false);
                setCollaboratorForm({
                  email: '',
                  role: 'viewer',
                });
                loadData();
              } catch (error) {
                console.error('Error creating collaborator:', error);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={collaboratorForm.email}
                  onChange={(e) => setCollaboratorForm({ ...collaboratorForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={collaboratorForm.role}
                  onChange={(e) => setCollaboratorForm({ ...collaboratorForm, role: e.target.value as 'viewer' | 'editor' | 'admin' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="editor">Editor (Can edit)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Send Invite
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddCollaborator(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!confirmDelete.id}
        message={`Are you sure you want to delete this ${confirmDelete.type === 'rule' ? 'rule' : 'collaborator'}?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteAction}
        onCancel={cancelDelete}
      />
    </div>
  );
}; 